import { z } from 'zod';
import type { FieldSpec, FormValues } from '@/components/patterns';

/**
 * Briques communes des formulaires du lot L5 (mêmes conventions que l'aire Accès) : les messages
 * génériques sont des clés de `patterns.form.errors` (`required`, `invalid`, `tooLong`), les
 * messages propres à un champ arrivent déjà traduits.
 */
export const NAME_MAX = 128;
export const DESCRIPTION_MAX = 2_000;

export const text = (value: unknown) => (typeof value === 'string' ? value.trim() : '');
export const nonEmpty = (value: unknown): value is string => text(value) !== '';

export const requiredText = (max = NAME_MAX) =>
  z.custom<string>(nonEmpty, 'required').refine((value) => text(value).length <= max, 'tooLong');

export const optionalText = (max = DESCRIPTION_MAX) =>
  z
    .unknown()
    .optional()
    .refine((value) => text(value).length <= max, 'tooLong');

/** Un choix obligatoire parmi une liste connue. */
export const oneOf = (choices: readonly string[]) =>
  z.custom<string>((value) => typeof value === 'string' && choices.includes(value), 'required');

/** `description` au corps seulement si elle n'est pas vide. */
export const withText = (values: FormValues, key: string, as = key) => {
  const value = text(values[key]);
  return value ? { [as]: value } : {};
};

/**
 * Objet COMPLET édité (`toJsonPatch` compare à l'original) : les clés non éditées restent, un champ
 * vidé est retiré — jamais mis à `''`.
 */
export function edited(entry: Record<string, unknown>, values: FormValues, keys: readonly string[]) {
  const next: Record<string, unknown> = { ...entry };
  for (const key of keys) {
    const value = text(values[key]);
    if (value) next[key] = value;
    else delete next[key];
  }
  return next;
}

/** Champ « description », libellé déjà traduit. */
export const descriptionField = (label: string): FieldSpec => ({
  name: 'description',
  kind: 'textarea',
  label,
  rows: 2,
  wide: true,
});

/** Code pays ISO 3166-1 alpha-2 (`FR`), tel que le rend le contrat. */
export const COUNTRY = /^[A-Z]{2}$/u;

/** Nom court d'une ressource d'exécution : minuscules, chiffres et tirets (il finit dans un nom k8s). */
export const RESOURCE_NAME = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/u;
